import React, { useEffect, useState } from "react";
import { useTheme } from "../hooks/useTheme";
import { SEASON_OPENS_AT, SCORING_OPENS_AT } from "../utils/season";
import wordmarkOnLight from "../assets/logo-wordmark-on-light.png";
import wordmarkOnDark from "../assets/logo-wordmark-on-dark.png";

// Two countdowns, full bleed across the top of the page.
//
// There are two dates and they are five days apart. College football opens on
// the week 0 Saturday; the fantasy game does not, because week 0 awards no
// points, so the first kickoff that scores is the first game of lineup week 1.
// One date for both would have a manager believe their lineup mattered on the
// opening Saturday.
//
// The second row carries the wordmark rather than the word. It is the one
// place on this page the product names itself, and it makes the distinction
// between the two rows visual rather than something to read twice.
//
// Light band, not the dark one this replaced: the wordmark's ink is navy, so
// on a navy band it disappeared. logo-wordmark-on-light.png is the mark cut
// for exactly this background; its on-dark twin exists for the other case.
//
// Deliberately not a countdown to a draft deadline. None exists: a draft is
// untimed, started by hand and nothing expires it, so counting down to one
// would have the app assert a rule it does not enforce.
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function remaining(target, now) {
  const ms = target - now;
  if (ms <= 0) return null;
  return {
    days: Math.floor(ms / DAY),
    hours: Math.floor((ms % DAY) / HOUR),
    minutes: Math.floor((ms % HOUR) / MINUTE),
  };
}

const format = (left) => {
  const parts = [];
  if (left.days) parts.push(`${left.days}d`);
  if (left.days || left.hours) parts.push(`${left.hours}h`);
  parts.push(`${left.minutes}m`);
  return parts.join(" ");
};

const labelText = (text) => (
  <span style={{ fontSize: 13, color: "var(--color-band-muted)", fontWeight: 600 }}>{text}</span>
);

function Row({ label, left, divider }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        paddingTop: divider ? 8 : 0,
        marginTop: divider ? 8 : 0,
        borderTop: divider ? "1px solid var(--color-band-border)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        {label}
      </div>
      <span
        style={{
          fontSize: 15,
          fontWeight: 800,
          color: "var(--color-band-strong)",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {format(left)}
      </span>
    </div>
  );
}

function SeasonCountdown() {
  const { theme } = useTheme();
  const footballAt = new Date(SEASON_OPENS_AT).getTime();
  const fantasyAt = new Date(SCORING_OPENS_AT).getTime();

  const compute = () => {
    const now = Date.now();
    return { football: remaining(footballAt, now), fantasy: remaining(fantasyAt, now) };
  };

  const [left, setLeft] = useState(compute);

  useEffect(() => {
    // A minute is enough: nothing here shows seconds, and a one second
    // interval would re-render 60 times a minute for no visible change.
    const id = setInterval(() => setLeft(compute()), MINUTE);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footballAt, fantasyAt]);

  if (!left.football && !left.fantasy) return null;

  return (
    <div style={{ backgroundColor: "var(--color-band-bg)", borderBottom: "1px solid var(--color-band-border)" }}>
      {/* Inner container carries the page's own max width and gutters, so the
          band runs to both screen edges while its content stays aligned with
          everything below it. */}
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "10px 16px" }}>
        {left.football && (
          <Row label={labelText("College football starts in")} left={left.football} />
        )}
        {left.fantasy && (
          <Row
            divider={Boolean(left.football)}
            label={
              <>
                <img
                  src={theme === "dark" ? wordmarkOnDark : wordmarkOnLight}
                  alt="Lineup"
                  style={{ height: 15, width: "auto", display: "block" }}
                />
                {labelText("starts in")}
              </>
            }
            left={left.fantasy}
          />
        )}
      </div>
    </div>
  );
}

export default SeasonCountdown;
